import { parseNumber } from './financialUtils';
import { MULTIPLE_MEAL_ONE_TIME_PRODUCTS } from './toastUtils';
import { formatingOptions } from '../formatingOptions';
import { CX_DISCOUNT_CODE_PREFIXES, isAdmin, isAllModuleAccessUser } from '../pages/constants';
import {
  Option,
  Fulfillment,
  FulfillmentProduct,
  Order,
  OrderFulfillmentProduct,
  OrderProduct,
  OrderStatus,
  OrderType,
  Orders,
  ProductCodesResponse,
  ProductType,
  PetPlanProduct,
  OneTimeModalType,
  Customer,
  Pet,
  Shipment,
} from '../pages/types';

import { MappedFulfillment } from '@/component/Claim/types';

export const hasFreshProduct = (products: PetPlanProduct[] | OrderProduct[]) => {
  for (const product of products) {
    if (product.code.startsWith('MEAL-F-')) {
      return true;
    }
  }
  return false;
};

export const hasUnKibbleProduct = (products: PetPlanProduct[] | OrderProduct[]) => {
  for (const product of products) {
    if (product.code.startsWith('MEAL-U-')) {
      return true;
    }
  }
  return false;
};

export const getOrder = (orders: Orders | undefined, orderId: string | undefined): any => {
  if (!orders) {
    return null;
  }
  return (
    orders.upcoming.find((order: Order) => order.id === orderId) ||
    orders.processing.find((order: Order) => order.id === orderId) ||
    orders.past.find((order: Order) => order.id === orderId)
  );
};

export const getFulfilledOrders = (order: Orders): Order[] => {
  const orders = [...order.past, ...order.processing, ...order.upcoming];
  return orders.filter(
    (order: Order) =>
      order.status === OrderStatus.COMPLETE ||
      order.status === OrderStatus.PARTIALLY_FULFILLED ||
      order.status === OrderStatus.FULFILLED ||
      order.status === OrderStatus.HISTORICAL ||
      order.status === OrderStatus.RUSH_ORDER
  );
};

export const getChargedOrders = (order: Orders): Order[] => {
  const orders = [...order.past, ...order.processing, ...order.upcoming];
  return orders.filter((order: Order) => !!order.charged);
};

export const getFulfilledOrderCount = (order: Orders): number => {
  return getFulfilledOrders(order).length;
};

export const getReplacedOrderCount = (order: Orders): number => {
  const orders = [...order.past, ...order.processing, ...order.upcoming];
  return orders.filter((order: Order) => order.order_type === OrderType.REPLACEMENT).length;
};

export const getCxCodeCount = (orderInput: Orders): number => {
  const orders = getChargedOrders(orderInput);
  let cxCodeCount = 0;
  for (const order of orders) {
    for (const discount of order.discounts) {
      if (discount?.discount) {
        if (discount.discount?.code) {
          if (
            CX_DISCOUNT_CODE_PREFIXES.some((prefix) => discount.discount.code.startsWith(prefix))
          ) {
            cxCodeCount++;
          }
        }
      }
    }
  }
  return cxCodeCount;
};

export const getWinbackDiscountEligiblility = (customerOrders: Orders): string => {
  const cutoffWindow = 90;
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - cutoffWindow);

  const ordersChargedAfterCutoff = [...customerOrders.processing, ...customerOrders.past].filter(
    (order) => !!order.charged && new Date(order.charged) > cutoffDate
  );

  return ![...customerOrders.upcoming, ...ordersChargedAfterCutoff].find((order) =>
    order.discounts.find((orderDiscount) => orderDiscount.discount.code.startsWith('WB-'))
  )
    ? 'Yes'
    : 'No';
};

export const getFreeExtraMealBagEligiblility = (customerOrders: Orders): string => {
  const cutoffWindow = 365;
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - cutoffWindow);

  const ordersChargedAfterCutoff = [...customerOrders.processing, ...customerOrders.past].filter(
    (order) =>
      [
        OrderStatus.PREVIEW,
        OrderStatus.PROCESSING,
        OrderStatus.CHARGING,
        OrderStatus.READY_FOR_FULFILLMENT,
        OrderStatus.FULFILLING,
        OrderStatus.PARTIALLY_FULFILLED,
        OrderStatus.FULFILLED,
        OrderStatus.COMPLETE,
      ].includes(order.status) &&
      order.charged &&
      new Date(order.charged) > cutoffDate
  );
  let count = 0;
  for (const order of [...customerOrders.upcoming, ...ordersChargedAfterCutoff]) {
    for (const product of order.products) {
      if (product.code.endsWith('BUFFER-1T-FREE')) {
        count += product.quantity;
      }
    }
  }
  return count < 2 ? 'Yes' : 'No';
};

export const validateOneTimeOrderProducts = (products: ProductCodesResponse[]) => {
  let mealProductCount = 0;
  products.forEach((product) => {
    if (product.type === ProductType.MEAL) {
      mealProductCount++;
    }
    if (mealProductCount > 1) {
      throw Error(MULTIPLE_MEAL_ONE_TIME_PRODUCTS);
    }
  });
};

export const filterProductsByPetMealType = (
  fullProducts: ProductCodesResponse[],
  initiatingModal: OneTimeModalType,
  selectedProducts?: Option[]
): Option[] => {
  if (!selectedProducts?.length) {
    return formatingOptions(fullProducts, { value: 'id', label: 'code' });
  }
  const freshCodes = ['CLIP', 'MEAL-F', 'FRESH'];
  const unKibbleCodes = ['SCOOP', 'MEAL-U-', 'UNKIBBLE'];
  const airdriedCodes = ['MEAL-A-'];
  const ambientCodes = [...unKibbleCodes, ...airdriedCodes];
  const bufferCodes = ['BUFFER'];
  const productsIncludeFresh = selectedProducts?.some((productOption) =>
    freshCodes.some((code) => productOption.label.includes(code))
  );
  const productsIncludeAmbient = selectedProducts?.some(
    (productOption) =>
      ambientCodes.some((code) => productOption.label.includes(code)) ||
      productOption.label.startsWith('A-') ||
      productOption.label.startsWith('U-')
  );

  const filteredProducts = fullProducts.filter((product) => {
    const { code } = product;

    if (productsIncludeFresh) {
      return !(
        ambientCodes.some((ambientCode) => code.includes(ambientCode)) ||
        code.startsWith('A-') ||
        code.startsWith('U-')
      );
    }

    if (productsIncludeAmbient) {
      const isFresh = freshCodes.some((freshCode) => code.includes(freshCode));
      const isBuffer = bufferCodes.some((bufferCode) => code.includes(bufferCode));

      if (initiatingModal === OneTimeModalType.ONE_TIME_PRODUCT) {
        return !isFresh && !isBuffer; // Buffer codes are not allowed in one time product modal
      }
      return !isFresh;
    }

    if (initiatingModal === OneTimeModalType.ONE_TIME_PRODUCT) {
      return !bufferCodes.some((bufferCode) => code.includes(bufferCode));
    }

    return true;
  });
  return formatingOptions(filteredProducts, { value: 'id', label: 'code' });
};

export const mapFulfillments = (order: Order): OrderFulfillmentProduct[] =>
  order.fulfillments
    .map((fulfillment: Fulfillment) =>
      fulfillment.products.map((product: FulfillmentProduct) => ({
        ...product,
        pet_id: product.pet_id || fulfillment.pet,
        isMealProduct: product.type === 'MEAL',
        tax_amount: parseNumber(
          order.products.find((orderProduct: OrderProduct) => orderProduct.code === product.code)
            ?.tax_amount
        ),
        fulfillment,
        order,
        unit_price: product.price.toFixed(2),
        discount_amount: parseNumber(product.discount_amount).toFixed(2),
        refund_amount: parseNumber(
          order.products.find((orderProduct: OrderProduct) => orderProduct.code === product.code)
            ?.refund_amount
        ).toFixed(2),
      }))
    )
    .flat()
    .sort((a: OrderFulfillmentProduct, b: OrderFulfillmentProduct) =>
      a.fulfillment.id.localeCompare(b.fulfillment.id)
    );

export const STANDARD_SHIPPING_FOR_FROZEN = {
  value: 'STANDARD',
  label: 'Orders including Fresh products are not eligible for faster shipping',
};

export const ECONOMY_SHIPPING_OPTION = {
  value: 'ECONOMY',
  label: 'Economy',
};

export const SHIPMENT_DISPLAY_VALUE: Record<string, string> = {
  ECONOMY: 'Economy',
  STANDARD: 'Standard',
  EXPEDITED: 'Expedited',
  NEXTDAY: 'Next Day',
  NEXTDAYAM: 'Next Day AM',
};

export const defaultShippingOption = (shippingOptions: Option[], customer: Customer) =>
  shippingOptions.find((option) => option.value === customer.default_shipping_method_id);

export const getPetNameForFulfillmentProduct = (
  product: OrderFulfillmentProduct | MappedFulfillment,
  pets: Customer['pets']
) => {
  const petId =
    product.pet_id ??
    (product.fulfillment.pet_ids.length === 1 ? product.fulfillment.pet_ids[0] : null);
  const pet = pets.find((pet: Pet) => pet.id === petId);
  return pet ? pet.name : '--';
};

export const getShipmentTooltipFields = (
  shipment: Shipment
): {
  mostRecentTimestamp?: Date;
  mostRecentStatus?: string;
} => {
  const statusFields = [
    'shipped',
    'in_transit',
    'out_for_delivery',
    'available_for_pickup',
    'delivered',
    'voided',
    'returned',
    'exception',
  ] as const;
  type StatusField = (typeof statusFields)[number];

  let mostRecentStatus: string | undefined;
  let mostRecentTimestamp: Date | undefined;

  for (const status of statusFields) {
    const timestamp = shipment[status as StatusField];
    if (
      timestamp &&
      (!mostRecentTimestamp || new Date(timestamp) > new Date(mostRecentTimestamp))
    ) {
      mostRecentTimestamp = new Date(timestamp);
      mostRecentStatus = status;
    }
  }

  return { mostRecentTimestamp, mostRecentStatus };
};

export const getModificationOrderEligibility = (order: Order): boolean => {
  return (
    order.status === OrderStatus.CANCELLED &&
    order.is_modified !== true &&
    parseNumber(order.total_refunds) === 0 &&
    parseNumber(order.total_shipping_price) === 0
  );
};

const WON_DISPUTE_STATUSES = ['won', 'prevented'];

export const isDisputeBlockingRefund = (disputeStatus: string | null): boolean => {
  if (disputeStatus === null) return false;
  if (isAdmin() || isAllModuleAccessUser()) return false;
  return !WON_DISPUTE_STATUSES.includes(disputeStatus.toLowerCase());
};

export const getRefundEligibility = (charge: any, order: any, disputeStatus: string | null) => {
  if (isDisputeBlockingRefund(disputeStatus)) {
    return false;
  }
  if (order.id.startsWith('SHO-')) {
    return false;
  }
  if (order?.order_type === 'REPLACEMENT') {
    return false;
  }
  return charge?.status === 'SUCCESS';
};
